
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Location } from 'vue-router';
import { CollectionProxy } from 'spraypaint/lib-esm/proxies';
import ObservedPromise from 'spa-ionata-admin/lib/services/ObservedPromise';
import { isCurrentRoute } from 'spa-ionata-admin/lib/services/compareRoutes';
import ErrorAlert from 'spa-ionata-admin/lib/components/ErrorAlert.vue';
import BusinessTile from '../components/BusinessTile.vue';
import TitledSection from '../components/TitledSection.vue';
import Business from '../models/Business';
import { categoriesModule, exportMarketsModule, sectorsModule } from '../store';

interface AZLink {
  value: string;
  text: string;
}

interface ResponsePagination {
  page: number;
  pages: number;
  count: number;
}

const sorter = (a: { text: string }, b: { text: string } | undefined) => {
  return a.text.localeCompare(b?.text || '');
};

@Component({ components: { TitledSection, BusinessTile, ErrorAlert } })
export default class SearchResults extends Vue {
  search = '';

  dataPromise = new ObservedPromise<CollectionProxy<Business>>();

  results: CollectionProxy<Business> | null = null;

  sectorsMap: Hash<boolean> = {};

  categoriesMap: Hash<boolean> = {};

  exportMarketsMap: Hash<boolean> = {};

  azLinks: AZLink[] = [];

  page = 1;

  isMounted = false;

  get isLoading(): boolean {
    return this.dataPromise.isPending;
  }

  get pagination(): ResponsePagination {
    const { page, pages, count } = (this.results?.meta?.pagination ||
      {}) as ResponsePagination;
    return {
      page,
      pages,
      count,
    };
  }

  get businesses(): Business[] {
    return this.results?.data || [];
  }

  get sectors(): SelectOptionItem[] {
    return sectorsModule.items
      .map(item => ({
        value: item.id || '',
        text: item.locale.name || '',
      }))
      .sort(sorter);
  }

  get categories(): SelectOptionItem[] {
    return categoriesModule.items
      .map(item => ({
        value: item.id || '',
        text: item.locale.name || '',
      }))
      .sort(sorter);
  }

  get exportMarkets(): SelectOptionItem[] {
    return exportMarketsModule.items
      .map(item => ({
        value: item.id || '',
        text: item.locale.title || '',
      }))
      .sort(sorter);
  }

  get sectorIds() {
    const { sectorsMap } = this;
    return Object.keys(sectorsMap).filter(id => sectorsMap[id]);
  }

  get categoriesIds() {
    const { categoriesMap } = this;
    return Object.keys(categoriesMap).filter(id => categoriesMap[id]);
  }

  get exportMarketIds() {
    const { exportMarketsMap } = this;
    return Object.keys(exportMarketsMap).filter(id => exportMarketsMap[id]);
  }

  get finalCategories(): SelectOptionItem[] {
    const { sectorIds, sectorsMap } = this;
    if (!sectorIds.length) {
      return this.categories;
    }
    return categoriesModule.items
      .filter(({ sector }) => sector && sectorsMap[sector.id || ''])
      .map(item => ({
        value: item.id || '',
        text: item.locale.name || '',
      }))
      .sort(sorter);
  }

  get querySearch(): string | undefined {
    const { q } = this.$route.query;
    if (typeof q === 'string') return q;
    return undefined;
  }

  get queryCategories(): string[] {
    const { c } = this.$route.query;
    if (typeof c === 'string') return c.split(',');
    return [];
  }

  get querySectors(): string[] {
    const { s } = this.$route.query;
    if (typeof s === 'string') return s.split(',');
    return [];
  }

  get queryExportMarkets(): string[] {
    const { em } = this.$route.query;
    if (typeof em === 'string') return em.split(',');
    return [];
  }

  get queryLetter(): string | undefined {
    const { l } = this.$route.query;
    if (typeof l === 'string') return l;
    return undefined;
  }

  get columns(): number {
    const { breakpoint } = this.$vuetify;
    if (breakpoint.lgAndUp) {
      return 4;
    }
    if (breakpoint.mdAndUp) {
      return 3;
    }
    if (breakpoint.smAndUp) {
      return 2;
    }
    return 2;
  }

  get rows() {
    const { columns, businesses } = this;
    const rows: Array<Business[]> = [];
    for (let n = 0; n < businesses.length / columns; n += 1) {
      const start = n * columns;
      rows.push(businesses.slice(start, start + columns));
    }
    return rows;
  }

  get keywords(): string[] {
    if (!this.querySearch) {
      return [];
    }
    const set = new Set();
    const keywords: string[] = [];
    this.querySearch.split(' ').forEach(kw => {
      if (!set.has(kw)) {
        set.add(kw);
        keywords.push(kw);
      }
    });
    return keywords;
  }

  get hasFilters() {
    return (
      this.keywords.length ||
      this.querySectors.length ||
      this.queryCategories.length ||
      this.queryExportMarkets.length ||
      this.queryLetter
    );
  }

  get locationFromNonAppliedFilters(): Location {
    const { keywords, sectorsMap, categoriesMap, exportMarketsMap } = this;
    const sectors = Object.keys(sectorsMap)
      .filter(i => sectorsMap[i])
      .join(',');
    const categories = Object.keys(categoriesMap)
      .filter(i => categoriesMap[i])
      .join(',');
    const exportMarkets = Object.keys(exportMarketsMap)
      .filter(i => exportMarketsMap[i])
      .join(',');
    const query: Partial<SearchQuery> = {};
    if (keywords.length) {
      query.q = keywords.join(' ');
    }
    if (sectors) {
      query.s = sectors;
    }
    if (categories) {
      query.c = categories;
    }
    if (this.queryLetter) {
      query.l = this.queryLetter;
    }
    if (exportMarkets) {
      query.em = exportMarkets;
    }
    return {
      path: this.$route.path,
      query,
      hash: 'results',
    };
  }

  get shouldApplyFilters() {
    if (!this.isMounted) return false;
    const location = this.locationFromNonAppliedFilters;
    return !isCurrentRoute(this.$router, location, undefined, true);
  }

  getQuery(keywords: string[] = []) {
    const query: Partial<SearchQuery> = {};
    if (keywords.length) {
      query.q = keywords.join(' ');
    }
    if (this.querySectors.length) {
      query.s = this.querySectors.join(',');
    }
    if (this.queryCategories.length) {
      query.c = this.queryCategories.join(',');
    }
    if (this.queryExportMarkets.length) {
      query.em = this.queryExportMarkets.join(',');
    }
    if (this.queryLetter) {
      query.l = this.queryLetter;
    }
    return query;
  }

  removeKeyword(idx: number) {
    const keywords = this.keywords.filter((k, index) => index !== idx);
    const query = this.getQuery(keywords);
    this.$router.push({ path: this.$route.path, query, hash: 'results' });
  }

  toggleSector(id: string) {
    this.sectorsMap = {
      ...this.sectorsMap,
      [id]: !this.sectorsMap[id],
    };
  }

  toggleCat(id: string) {
    this.categoriesMap = {
      ...this.categoriesMap,
      [id]: !this.categoriesMap[id],
    };
  }

  toggleExportMarket(id: string) {
    this.exportMarketsMap = {
      ...this.exportMarketsMap,
      [id]: !this.exportMarketsMap[id],
    };
  }

  fetchBusinesses() {
    const {
      sectorIds,
      categoriesIds,
      exportMarketIds,
      sectorsMap,
      keywords,
      queryLetter,
      page,
    } = this;
    const promise = (async () => {
      let catIds = categoriesIds;
      if (categoriesIds.length === 0 && sectorIds.length) {
        await categoriesModule.fetchItemsIfNeeded();
        catIds = categoriesModule.items
          .filter(({ sector }) => sector && sectorsMap[sector.id || ''])
          .map(({ id }) => id || '');
      }
      const where: Hash<string> = { is_public: 'true' };
      const search = keywords.join(' ') || undefined;
      if (search) {
        where['search'] = search;
      }
      const isNumeric = queryLetter === '9';
      if (isNumeric) {
        where['name.startswithalpha'] = 'false';
      } else if (queryLetter) {
        where['name.istartswith'] = queryLetter;
      }
      const categories = catIds.join(',') || undefined;
      if (categories) {
        where['categories.in'] = categories;
      }
      const exportMarkets = exportMarketIds.join(',') || undefined;
      if (exportMarkets) {
        where['export_markets.in'] = exportMarkets;
      }
      return Business.where(where).page(page).per(20).all();
    })();
    const dataPromise = new ObservedPromise(promise);
    this.dataPromise = dataPromise;
    promise
      .then(() => {
        if (this.dataPromise === dataPromise) {
          this.results = this.dataPromise.value || null;
        }
      })
      .catch(() => {
        // ignore
      });
  }

  maybeSearch() {
    const keywords = this.keywords.concat([this.search || '']).filter(s => s);
    const query = this.getQuery(keywords);
    const location: Location = {
      path: this.$route.path,
      query,
      hash: 'results',
    };
    if (!isCurrentRoute(this.$router, location, undefined, true)) {
      this.search = '';
      this.$router.push(location);
    }
  }

  clearFilters() {
    this.sectorsMap = {};
    this.categoriesMap = {};
    this.search = '';
    const location: Location = {
      path: this.$route.path,
      query: {},
      hash: 'results',
    };
    if (!isCurrentRoute(this.$router, location, undefined, true)) {
      this.$router.push(location);
      this.fetchBusinesses();
    }
  }

  mounted() {
    sectorsModule.fetchItemsIfNeeded();
    categoriesModule.fetchItemsIfNeeded();
    exportMarketsModule.fetchItemsIfNeeded();

    for (let n = 97; n < 123; n += 1) {
      this.azLinks.push({
        value: String.fromCharCode(n),
        text: String.fromCharCode(n - 32),
      });
    }
    this.azLinks.push({
      value: '9',
      text: '0-9',
    });

    // 👇 Ensure queried letter is valid
    const { queryLetter: l } = this;
    if (l && this.azLinks.findIndex(item => item.value === l) === -1) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { l: _, query } = this.$route.query;
      this.$router.replace({ query: query as HashAny });
      // return;
    }

    if (this.querySectors.length) {
      const sectorsMap: Hash<boolean> = {};
      this.querySectors.forEach(id => {
        sectorsMap[id] = true;
      });
      this.sectorsMap = { ...this.sectorsMap, ...sectorsMap };
    }

    if (this.queryCategories.length) {
      const categoriesMap: Hash<boolean> = {};
      this.queryCategories.forEach(id => {
        categoriesMap[id] = true;
      });
      this.categoriesMap = { ...this.categoriesMap, ...categoriesMap };
    }

    if (this.queryExportMarkets.length) {
      const exportMarketsMap: Hash<boolean> = {};
      this.queryExportMarkets.forEach(id => {
        exportMarketsMap[id] = true;
      });
      this.exportMarketsMap = { ...this.exportMarketsMap, ...exportMarketsMap };
    }

    // TODO: ensure sectorsMap and categoriesMap don't include ids that are missing from the modules

    this.fetchBusinesses();
    this.isMounted = true;
  }

  @Watch('querySearch')
  // @Watch('page')
  onQueryChanged() {
    this.fetchBusinesses();
  }
  @Watch('page')
  onPageChanged() {
    this.fetchBusinesses();
  }

  @Watch('queryLetter')
  onLetterChange() {
    this.sectorsMap = {};
    this.categoriesMap = {};
    this.exportMarketsMap = {};
    this.page = 1;
    this.fetchBusinesses();
  }

  @Watch('shouldApplyFilters', { immediate: true })
  onFiltersChanged(shouldApply: boolean) {
    if (shouldApply) {
      this.$router.replace(this.locationFromNonAppliedFilters);
      this.fetchBusinesses();
    }
  }
}
